import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import SharedNavbar from '@/components/feature/SharedNavbar';
import SharedFooter from '@/components/feature/SharedFooter';
import RequireReseller from '@/components/feature/RequireReseller';
import { Badge } from '@/components/ui/Badge';

interface ResellerLayoutProps {
  children: React.ReactNode;
  title?: string;
}

export default function ResellerLayout({ children, title }: ResellerLayoutProps) {
  const { resellerProfile, logout } = useAuth();
  const location = useLocation();

  const tabs = [
    { href: '/reseller', label: 'Dashboard', icon: 'ri-dashboard-line' },
    { href: '/reseller/orders', label: 'Orders', icon: 'ri-shopping-bag-line' },
    { href: '/reseller/order/new', label: 'Place Order', icon: 'ri-add-box-line' },
    { href: '/reseller/profile', label: 'Profile', icon: 'ri-user-line' },
  ];

  const isActive = (href: string) => {
    if (href === '/reseller') return location.pathname === '/reseller';
    return location.pathname.startsWith(href);
  };

  return (
    <RequireReseller>
      <SharedNavbar />
      <div className="w-full min-h-screen bg-[#FAFAF9] pt-24 pb-20 px-4 md:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Reseller Summary */}
          <div className="bg-white rounded-2xl p-6 md:p-8 border border-[#E8E8E6] mb-6">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
              <div className="w-16 h-16 rounded-full bg-[#F0EFED] flex items-center justify-center text-xl font-serif text-[#7B1113]">
                {resellerProfile?.name?.charAt(0) || 'R'}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h1 className="font-serif text-xl font-bold text-[#2A2A2A]">{resellerProfile?.name}</h1>
                  <Badge tone={resellerProfile?.status === 'active' ? 'success' : 'neutral'}>{resellerProfile?.status}</Badge>
                </div>
                <p className="text-sm text-[#8A8A8A] capitalize">{resellerProfile?.type} Partner{resellerProfile?.district ? ` · ${resellerProfile.district}` : ''}</p>
                {!!resellerProfile?.wholesaleDiscountPct && (
                  <span className="inline-block mt-2 text-xs text-[#2D6A4F] font-medium">
                    {resellerProfile.wholesaleDiscountPct}% wholesale discount applied at checkout
                  </span>
                )}
              </div>
            </div>
          </div>

          {title && <h2 className="font-serif text-lg font-bold text-[#2A2A2A] mb-4">{title}</h2>}

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl border border-[#E8E8E6] overflow-hidden">
                {tabs.map((tab) => (
                  <Link
                    key={tab.href}
                    to={tab.href}
                    className={`flex items-center gap-2 px-5 py-3.5 text-sm transition-colors ${
                      isActive(tab.href)
                        ? 'text-[#7B1113] bg-[#7B1113]/5 border-l-2 border-[#7B1113]'
                        : 'text-[#8A8A8A] hover:text-[#2A2A2A] hover:bg-[#F0EFED]'
                    }`}
                  >
                    <i className={tab.icon}></i>
                    {tab.label}
                  </Link>
                ))}
                <button
                  onClick={logout}
                  className="w-full flex items-center gap-2 px-5 py-3.5 text-sm text-[#8A8A8A] hover:text-[#7B1113] hover:bg-[#7B1113]/5 transition-colors cursor-pointer"
                >
                  <i className="ri-logout-box-r-line"></i>
                  Sign Out
                </button>
              </div>
            </div>

            {/* Main */}
            <div className="lg:col-span-3">{children}</div>
          </div>
        </div>
      </div>
      <SharedFooter />
    </RequireReseller>
  );
}
